import { Component, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { faCalendarDays, faEye } from '@fortawesome/free-solid-svg-icons';
import { TableAction, TableColumn } from '../shared/models/table-models';
import { Table } from '../shared/table/table';
import { Popup } from '../../core/shared/components/popup/popup';
import { OnboradingService } from '../onboarding/shared/service/onboarding.service';

type CnicTab = 'expiring' | 'updated';

@Component({
  selector: 'app-cnic-expiry',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, Table, Popup],
  templateUrl: './cnic-expiry.html',
  styleUrl: './cnic-expiry.css',
})
export class CnicExpiry implements OnInit {
  /** Same lifetime marker the onboarding person forms save. */
  readonly LIFETIME_CNIC_DATE = '3000-01-01';
  readonly ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf';
  private readonly MAX_FILE_SIZE_MB = 2;

  activeTab: CnicTab = 'expiring';

  expiringList: any[] = [];
  updatedList: any[] = [];

  today = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');

  // ================= UPDATE (RO) =================
  showUpdatePopup = false;
  isSubmitting = false;
  submitted = false;
  form = this.emptyForm();
  docFile: File | null = null;
  docPreview = '';

  // ================= APPROVAL (CM) =================
  showApprovalPopup = false;
  isApproving = false;
  selectedUpdated: any = null;
  approvalDocSrc = '';
  approvalDocIsImage = false;

  constructor(
    private translate: TranslateService,
    private onboardingService: OnboradingService,
    private toastr: ToastrService,
  ) {}

  // ================= ROLES =================
  private get currentRole(): string {
    return (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
      .trim()
      .toUpperCase();
  }

  /** Relationship Officer (LNLY_RO) submits the renewed CNIC. */
  get isRelationshipOfficer(): boolean {
    return this.currentRole === 'LNLY_RO';
  }

  /** Credit Manager (LNLY_CM) approves the submitted CNIC. */
  get isCreditManager(): boolean {
    return this.currentRole === 'LNLY_CM';
  }

  // ================= INIT =================
  ngOnInit(): void {
    this.loadExpiringList();
    this.loadUpdatedList();
  }

  switchTab(tab: CnicTab): void {
    this.activeTab = tab;
  }

  // ================= TABLE =================
  expiringColumns: TableColumn[] = [
    { key: 'name', label: 'CNIC_EXPIRY.NAME' },
    { key: 'fthrHsbndNm', label: 'CNIC_EXPIRY.FATHER_HUSBAND_NAME' },
    { key: 'cnicNum', label: 'CNIC_EXPIRY.CNIC' },
    { key: 'expiryDisplay', label: 'CNIC_EXPIRY.CNIC_EXPIRY' },
    { key: 'instNm', label: 'CNIC_EXPIRY.INSTITUTION' },
  ];

  updatedColumns: TableColumn[] = [
    { key: 'cnicNum', label: 'CNIC_EXPIRY.CNIC' },
    { key: 'cnicIssueDt', label: 'CNIC_EXPIRY.ISSUE_DATE', type: 'date' },
    { key: 'expiryDisplay', label: 'CNIC_EXPIRY.EXPIRY_DATE' },
    { key: 'crtdBy', label: 'CNIC_EXPIRY.SUBMITTED_BY' },
    { key: 'crtdDt', label: 'CNIC_EXPIRY.SUBMITTED_DATE', type: 'date' },
  ];

  // Only the Relationship Officer can act on this list; with no actions the
  // table drops its Action column entirely for every other role.
  expiringActions: TableAction[] = this.isRelationshipOfficer
    ? [
        {
          icon: faCalendarDays,
          tooltip: 'CNIC_EXPIRY.UPLOAD_CNIC',
          class: 'flex items-center justify-center text-indigo-600',
          onClick: (row: any) => this.openUpdatePopup(row),
        },
      ]
    : [];

  updatedActions: TableAction[] = [
    {
      icon: faEye,
      tooltip: 'COMMON.VIEW',
      class: 'flex items-center justify-center text-amber-500',
      onClick: (row: any) => this.openApprovalPopup(row),
    },
  ];

  // ================= LOAD =================
  /** GET /clients/get-cnic-exp-list — persons whose CNIC is expiring (CnicExpVw). */
  loadExpiringList(): void {
    this.onboardingService.getCnicExpList().subscribe({
      next: (res: any) => {
        this.expiringList = this.toRows(res, 'cncExpryDt');
      },
      error: () => {
        this.expiringList = [];
      },
    });
  }

  /** GET /clients/get-inprocs-cnic-list — submitted CNICs pending approval (ClntCnicExpRespDTO). */
  loadUpdatedList(): void {
    this.onboardingService.getInProcsCnicList().subscribe({
      next: (res: any) => {
        this.updatedList = this.toRows(res, 'cnicExpryDt');
      },
      error: () => {
        this.updatedList = [];
      },
    });
  }

  private toRows(res: any, expiryKey: string): any[] {
    if (!res || res.error) return [];

    const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

    return data.map((item: any) => ({
      ...item,
      expiryDisplay: this.formatExpiry(item[expiryKey]),
    }));
  }

  private formatExpiry(value: any): string {
    if (!value) return '';

    if (String(value).slice(0, 10) === this.LIFETIME_CNIC_DATE) {
      return this.translate.instant('CNIC_EXPIRY.LIFETIME');
    }

    try {
      return formatDate(value, 'dd-MMM-yyyy', 'en-US');
    } catch {
      return '';
    }
  }

  // ================= UPDATE POPUP (RO) =================
  private emptyForm() {
    return {
      cnicNum: '' as string | number,
      cnicIssueDt: '',
      cnicExpryDt: '',
      isLifetime: false,
    };
  }

  openUpdatePopup(row: any): void {
    if (!this.isRelationshipOfficer) return;

    this.form = { ...this.emptyForm(), cnicNum: row.cnicNum };
    this.clearFile();
    this.submitted = false;
    this.showUpdatePopup = true;
  }

  onLifetimeChange(): void {
    if (this.form.isLifetime) {
      this.form.cnicExpryDt = '';
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) return;

    if (!this.ACCEPTED_FILE_TYPES.split(',').includes(file.type)) {
      this.toastr.error(this.translate.instant('CNIC_EXPIRY.INVALID_FILE'));
      return;
    }

    if (file.size / (1024 * 1024) > this.MAX_FILE_SIZE_MB) {
      this.toastr.error(
        this.translate.instant('CNIC_EXPIRY.FILE_TOO_LARGE', { size: this.MAX_FILE_SIZE_MB }),
      );
      return;
    }

    this.clearFile();
    this.docFile = file;

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        // Ignore a slow read if the user already picked/removed another file.
        if (this.docFile === file) {
          this.docPreview = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  clearFile(): void {
    this.docFile = null;
    this.docPreview = '';
  }

  get isExpiryMissing(): boolean {
    return !this.form.isLifetime && !this.form.cnicExpryDt;
  }

  /** POST /clients/update-cnic-exp — multipart ClntCnicExpDTO + CNIC document. */
  submitCnic(): void {
    if (this.isSubmitting) return;

    this.submitted = true;

    if (!this.form.cnicNum || !this.form.cnicIssueDt || this.isExpiryMissing) {
      this.toastr.error(this.translate.instant('CNIC_EXPIRY.REQUIRED_FIELDS'));
      return;
    }

    if (!this.docFile) {
      this.toastr.error(this.translate.instant('CNIC_EXPIRY.DOCUMENT_REQUIRED'));
      return;
    }

    const formData = new FormData();
    formData.append('cnicNum', String(this.form.cnicNum));
    formData.append('cnicIssueDt', this.form.cnicIssueDt);
    formData.append(
      'cnicExpryDt',
      this.form.isLifetime ? this.LIFETIME_CNIC_DATE : this.form.cnicExpryDt,
    );
    formData.append('delFlg', 'false');
    formData.append('docImg', this.docFile);

    this.isSubmitting = true;

    this.onboardingService.updateCnicExp(formData).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;

        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('CNIC_EXPIRY.SUBMIT_SUCCESS'));
        this.showUpdatePopup = false;
        this.clearFile();
        this.loadExpiringList();
        this.loadUpdatedList();
      },
      error: () => {
        this.isSubmitting = false;
        this.toastr.error(this.translate.instant('CNIC_EXPIRY.SUBMIT_FAILED'));
      },
    });
  }

  // ================= APPROVAL POPUP (CM) =================
  openApprovalPopup(row: any): void {
    this.selectedUpdated = row;

    const base64 = this.cleanBase64(row?.docImg);
    const mimeType = this.detectMimeType(base64);

    this.approvalDocIsImage = mimeType.startsWith('image/');
    this.approvalDocSrc = base64 ? `data:${mimeType};base64,${base64}` : '';
    this.showApprovalPopup = true;
  }

  private cleanBase64(value: any): string {
    let base64 = String(value ?? '').trim();
    const commaIndex = base64.indexOf(',');

    if (base64.startsWith('data:') && commaIndex !== -1) {
      base64 = base64.substring(commaIndex + 1);
    }

    return base64.replace(/["\s]/g, '');
  }

  /** Guesses the stored document's mime type from its base64 signature. */
  private detectMimeType(base64: string): string {
    if (base64.startsWith('/9j/')) return 'image/jpeg';
    if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
    if (base64.startsWith('JVBERi0')) return 'application/pdf';

    return 'application/octet-stream';
  }

  /** Opens a non-image document (PDF) in a new tab. */
  viewDocument(): void {
    const match = /^data:(.+?);base64,(.*)$/.exec(this.approvalDocSrc);
    if (!match) return;

    try {
      const bytes = Uint8Array.from(atob(match[2]), (char) => char.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: match[1] }));

      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      this.toastr.error(this.translate.instant('CNIC_EXPIRY.DOCUMENT_LOAD_FAILED'));
    }
  }

  /** POST /clients/aprv-cnic-exp/{cnicNum} */
  approveCnic(): void {
    if (!this.isCreditManager || this.isApproving || !this.selectedUpdated?.cnicNum) return;

    this.isApproving = true;

    this.onboardingService.approveCnicExp(this.selectedUpdated.cnicNum).subscribe({
      next: (res: any) => {
        this.isApproving = false;

        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('CNIC_EXPIRY.APPROVE_SUCCESS'));
        this.showApprovalPopup = false;
        this.selectedUpdated = null;
        this.loadExpiringList();
        this.loadUpdatedList();
      },
      error: () => {
        this.isApproving = false;
        this.toastr.error(this.translate.instant('CNIC_EXPIRY.APPROVE_FAILED'));
      },
    });
  }
}
